import clsx from 'clsx';
import React from 'react';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import IconCheckMark from '@components/Images/Rebranding/IconCheckMark';
import OfferPrice from '@components/pages/SubscribeTest/OfferSelection/OfferTable/OfferPrice';
import useEventTracking from '@hooks/useEventTracking';
import { subscribe_one_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import type { OfferOption } from '@utils/backend-api/subscribe';

import type {
  SubscribeMatrixBlockConfig,
  SubscribeMatrixColumnKind,
  SubscribeMatrixCustomColumn,
  SubscribeMatrixOffers,
} from '../interfaces';
import subscribeParams from '../utils/subscribeParams';

const COLUMN_KIND_LABELS: Record<SubscribeMatrixColumnKind, string> = {
  annual: 'Annual',
  custom: 'Custom',
  monthly: 'Monthly',
  pro: 'Pro',
};

interface SubscribeMatrixCustomProps {
  config?: SubscribeMatrixBlockConfig;
  offers?: SubscribeMatrixOffers;
  redirectTo?: string | null;
}

interface CustomColumnHeaderProps {
  column: SubscribeMatrixCustomColumn;
  offer?: OfferOption | null;
  onOfferClick: (offer: OfferOption) => void;
  redirectTo?: string | null;
}

const getColumnOffer = (
  column: SubscribeMatrixCustomColumn,
  offers?: SubscribeMatrixOffers
): OfferOption | null | undefined => {
  const columnOffer = offers?.custom_offers?.[column.id];
  if (columnOffer) return columnOffer;
  if (column.kind === 'monthly') return offers?.monthly_offer;
  if (column.kind === 'annual') return offers?.annual_offer;
  if (column.kind === 'pro') return offers?.pro_offer;
  return undefined;
};

const isFeatureIncluded = (
  included: Record<string, boolean> | undefined,
  columnId: string
): boolean => {
  if (!included) return false;
  if (included[columnId]) return true;

  const underscored = columnId.replace(/-/g, '_');
  if (included[underscored]) return true;

  return Boolean(included[columnId.replace(/_/g, '-')]);
};

const getColumnTitle = (column: SubscribeMatrixCustomColumn, offer?: OfferOption | null) =>
  column.title?.trim() || offer?.title || COLUMN_KIND_LABELS[column.kind];

const shouldShowCta = (column: SubscribeMatrixCustomColumn, offer?: OfferOption | null) => {
  if (!offer?.code) return false;
  if (column.kind === 'custom') return Boolean(column.show_cta);
  return true;
};

const CustomColumnHeader = ({
  column,
  offer,
  onOfferClick,
  redirectTo,
}: CustomColumnHeaderProps) => {
  const title = getColumnTitle(column, offer);
  const isPro = Boolean(offer?.isPro);
  const showCta = shouldShowCta(column, offer);
  const Button = isPro ? ProButton : PrimaryButton;

  return (
    <div className="relative flex w-full flex-1 flex-col items-center md:max-w-[190px] lg:max-w-[270px]">
      <h3 className="m-0 text-center font-suisse-works text-body-lg text-black sm:text-headline-xxs">
        {title}
      </h3>
      {offer && <OfferPrice className="mt-8" offer={offer} />}
      {showCta && offer?.code && (
        <Button
          aria-label={`Subscribe to ${title}`}
          as="link"
          className="mt-auto w-full"
          href={subscribe_one_path(subscribeParams({ offerCode: offer.code, redirectTo }))}
          onClickCapture={() => onOfferClick(offer)}
          size="small"
        >
          Subscribe
        </Button>
      )}
    </div>
  );
};

const SubscribeMatrixCustom = ({ config, offers, redirectTo }: SubscribeMatrixCustomProps) => {
  const { trackEvent } = useEventTracking({ location: TrackingLocation.OfferTable });
  const columns = config?.custom_columns ?? [];
  const rows = config?.custom_rows ?? [];

  const handleOfferClick = (offer: OfferOption) => {
    trackEvent(
      click({
        clickTarget: ClickTarget.Offer,
        clickText: ClickTarget.Subscribe,
        offerCode: offer.code,
      })
    );
  };

  if (columns.length === 0) return null;

  return (
    <section
      aria-label="Subscription comparison"
      className={clsx('mt-24 flex flex-col', {
        'lg:mx-auto lg:max-w-[calc(100%-275px)]': columns.length === 2,
        'lg:mx-auto lg:max-w-[calc(100%-550px)]': columns.length === 1,
      })}
      data-testid="subscribe-matrix-custom"
    >
      <div className="border-0 border-b border-solid border-grayscale-beige bg-white py-24 dark:bg-[var(--white)]">
        <div className="flex justify-center gap-32 md:justify-end">
          {columns.map((column) => (
            <CustomColumnHeader
              key={column.id}
              column={column}
              offer={getColumnOffer(column, offers)}
              onOfferClick={handleOfferClick}
              redirectTo={redirectTo}
            />
          ))}
        </div>
      </div>
      <div className="flex w-full flex-col">
        {rows.map((row) => (
          <div
            key={row.id}
            className="relative flex flex-col items-center gap-16 border-0 border-b border-solid border-grayscale-beige py-16 last:border-b-0 md:flex-row md:justify-between md:gap-0"
          >
            <div className="relative m-0 font-suisse-intl text-overline-md">{row.label}</div>
            <div className="flex w-full flex-row justify-around gap-32 md:w-auto md:justify-normal">
              {columns.map((column) => {
                const offer = getColumnOffer(column, offers);
                return (
                  <div key={column.id} className="text-center md:w-[190px] lg:w-[270px]">
                    {isFeatureIncluded(row.included, column.id) && (
                      <IconCheckMark
                        className={clsx('h-[18px]', {
                          'text-primary-magenta': !offer?.isPro,
                          'text-pro-gold': offer?.isPro,
                        })}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default SubscribeMatrixCustom;
